/* eslint-disable @typescript-eslint/no-empty-function */
import { ActionsDropdown } from '@molecules';
import { PageListProps, PageListTemplate } from '@templates';
import { TableColumnType, Tag } from 'antd';
import { format, parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CouponPhysic } from 'src/models';

import {
  getInfoStatus,
  parseOrderFilter,
  tableColumnCouponPhysicOrderDefined,
} from './utils/table.couponPhysic';

export interface TableCouponPhysicProps {
  filterComponent: React.ReactNode;
  data?: CouponPhysic.TCouponResponse;
  onChangePagination?: (take: number, skip: number, pagination: number) => void;
  onEditCoupon?: (value: CouponPhysic.ICoupon) => void;
  onDeleteCoupon?: (value: CouponPhysic.ICoupon) => void;
  onPreviewCoupon?: (value: CouponPhysic.ICoupon) => void;
  onChangeOrder?: (order: string) => void;
  isLoading: boolean;
}

export type TColumnCouponPhysicOrder = {
  column: string;
  inverse: boolean;
};

export const TableCouponPhysic: React.FC<TableCouponPhysicProps> = ({
  filterComponent,
  data,
  onChangePagination,
  isLoading,
  onChangeOrder,
}) => {
  const [orderColumn, setOrderColumn] =
    useState<TColumnCouponPhysicOrder | null>(null);
  const navigate = useNavigate();

  const handleChangeOrder = useCallback(
    ({ key }: Pick<TableColumnType<CouponPhysic.ICoupon>, 'key'>) => {
      if (!key) return;

      const column = parseOrderFilter(String(key));

      if (!orderColumn || orderColumn.column !== column) {
        setOrderColumn({ column, inverse: false });
        return;
      }

      if (orderColumn.inverse) {
        setOrderColumn(null);
        return;
      }

      setOrderColumn({ column, inverse: true });
    },
    [orderColumn]
  );

  useEffect(() => {
    if (!onChangeOrder || isLoading || !data) return;
    onChangeOrder(
      !orderColumn
        ? ''
        : `[${orderColumn.column},${orderColumn.inverse ? 'DESC' : 'ASC'}]`
    );
  }, [orderColumn]);

  const handleViewCoupon = (coupon: CouponPhysic.ICoupon) => {
    navigate('/screens/physic/coupons/read', { state: { coupon } });
  };

  const headerColumns: PageListProps<CouponPhysic.ICoupon>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '100px',
      render: (coupon: CouponPhysic.ICoupon['id']) => {
        return coupon;
      },
    },
    {
      title: 'Código do Cupom',
      dataIndex: 'code',
      key: 'code',
      width: '200px',
      render: (code: CouponPhysic.ICoupon['code']) => {
        return code;
      },
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      width: '260px',
      render: (description: CouponPhysic.ICoupon['description']) => {
        return description;
      },
    },
    {
      title: 'Tipo de Desconto',
      dataIndex: 'discount',
      key: 'discount',
      width: '150px',
      render: (discount: CouponPhysic.ICoupon['discount']) => {
        return <div>{CouponPhysic.getLabelType(discount.type)}</div>;
      },
    },
    {
      title: 'Valor',
      dataIndex: 'discount',
      key: 'discount',
      width: '150px',
      render: (discount: CouponPhysic.ICoupon['discount']) => {
        return discount.type === CouponPhysic.TypeEnum.FIXED
          ? new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(discount.value)
          : `${discount.value}%`;
      },
    },
    {
      title: 'Data de início',
      dataIndex: 'startDate',
      key: 'startDate',
      width: '156px',
      sorter: true,
      sortOrder: tableColumnCouponPhysicOrderDefined('startDate', orderColumn),
      onHeaderCell: columns => {
        return {
          onClick: () => {
            handleChangeOrder(columns);
          },
        };
      },
      render: (startDate: CouponPhysic.ICoupon['startDate']) => {
        // return startDate;
        return <div>{format(new Date(startDate), 'dd/MM/yyyy')}</div>;
      },
    },
    {
      title: 'Data Final',
      dataIndex: 'endDate',
      key: 'endDate',
      width: '156px',
      sorter: true,
      sortOrder: tableColumnCouponPhysicOrderDefined('endDate', orderColumn),
      onHeaderCell: columns => {
        return {
          onClick: () => {
            handleChangeOrder(columns);
          },
        };
      },
      render: (endDate: CouponPhysic.ICoupon['endDate']) => {
        // return endDate;
        return <div>{format(parseISO(endDate), 'dd/MM/yyyy')}</div>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '150px',
      render: (status: CouponPhysic.ICoupon['status']) => {
        const { Icon, color, text } = getInfoStatus(status);

        return (
          <Tag icon={<Icon />} color={color}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: 'Ações',
      key: 'action',
      dataIndex: 'index',
      width: '2%',
      render: (_text, record) => (
        <ActionsDropdown
          hideDelete
          onClickEditOption={() => {
            navigate(`/screens/physic/coupons/${record.id}`);
          }}
          onClickPreviewOption={() => {
            handleViewCoupon(record);
          }}
        />
      ),
    },
  ];

  if (!data && isLoading) return null;

  return (
    <PageListTemplate
      filterComponent={filterComponent}
      columns={headerColumns}
      data={data}
      loading={isLoading}
      onChange={onChangePagination}
    />
  );
};
